<template>
  <div>
    <!-- HEADER -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <h6 class="mb-0">Configuration Groups</h6>

      <!-- USERSUBGROUP MODE : EDIT / SAVE / CANCEL -->
      <div v-if="usersubgroup_id">
        <i
          v-if="!editMode"
          class="fas fa-edit text-info"
          style="cursor:pointer;font-size:18px"
          @click="enableEditMode"
        ></i>

        <div v-else>
          <b-button size="sm" variant="success" class="mr-2"
            @click="saveUsersubgroupGroup">
            Save
          </b-button>

          <b-button size="sm" variant="secondary"
            @click="cancelEditMode">
            Cancel
          </b-button>
        </div>
      </div>

      <!-- NORMAL MODE : ADD -->
      <b-button
        size="sm"
        variant="primary"
        v-if="!usersubgroup_id"
        @click="openAddGroupModal"
      >
        Add Configuration Group
      </b-button>
    </div>

    <!-- GROUP LIST -->
    <div class="groupData">
      <b-overlay :show="loading" spinner-small rounded>
        <table class="table table-bordered table-striped">
          <thead class="thead-dark">
            <tr>
              <th v-if="usersubgroup_id" style="width:40px"></th>
              <th>Group Name</th>
              <th v-if="!usersubgroup_id" style="width:90px"></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="group in groups" :key="group.id">
              <!-- RADIO -->
              <td v-if="usersubgroup_id" class="text-center align-middle">
                <b-form-radio
                  v-model="selectedGroupId"
                  :value="group.id"
                  name="configurationGroup"
                  :disabled="!editMode"
                />
              </td>

              <!-- NAME -->
              <td class="align-middle">
                <span
                  class="text-info font-weight-bolder btn p-0 m-0"
                  @click="$bvModal.show('UsergroupOptions' + group.id)"
                >
                  {{ group.name }}
                </span>
              

                <b-modal
                  :id="'UsergroupOptions' + group.id"
                  :title="group.name + ' Configuration'"
                  no-close-on-backdrop
                  no-close-on-keyboard
                  hide-footer
                  modal-class="coursesOptions"
                >
                  <UsergroupsOptions
                    :usergroup_name="group.name"
                    :usergroup_id="usergroup_id"
                    :usersubgroup_id="usersubgroup_id"
                    :group_id="group.id"
                  />
                </b-modal>
              </td>

              <!-- COPY / EDIT ICONS (NORMAL MODE) -->
              <td v-if="!usersubgroup_id" class="text-center align-middle">
                <i
                  class="fas fa-copy text-success mr-2"
                  style="cursor:pointer;font-size:18px !important;"
                  title="Duplicate"
                  @click="openDuplicateModal(group)"
                ></i>
                <i
                  class="fas fa-edit text-info"
                  style="cursor:pointer;font-size:18px !important"
                  @click="openEditGroupModal(group)"
                ></i>
              </td>
            </tr>

            <tr v-if="!groups.length && !loading">
              <td :colspan="usersubgroup_id ? 2 : 2"
                  class="text-center text-muted">
                No configuration groups found
              </td>
            </tr>
          </tbody>
        </table>
      </b-overlay>
    </div>

    <!-- DUPLICATE MODAL -->
    <b-modal v-model="showDuplicateModal" title="Duplicate Configuration Group" hide-footer v-if="!usersubgroup_id">
      <b-form @submit.prevent="confirmDuplicate">
        <b-form-group label="New Group Name">
          <b-form-input v-model="duplicateName" required />
        </b-form-group>
        <div class="text-right">
          <b-button variant="secondary" @click="showDuplicateModal = false">Cancel</b-button>
          <b-button variant="primary" type="submit" class="ml-2">Duplicate</b-button>
        </div>
      </b-form>
    </b-modal>

    <!-- ADD / EDIT MODAL (NORMAL MODE ONLY) -->
    <b-modal
      v-model="showModal"
      :title="modalTitle"
      hide-footer
      v-if="!usersubgroup_id"
    >
      <b-form @submit.prevent="submitGroup">
        <b-form-group label="Group Name">
          <b-form-input v-model="groupName" required />
        </b-form-group>

        <div class="text-right">
          <b-button variant="secondary" @click="closeModal">
            Cancel
          </b-button>
          <b-button variant="primary" type="submit" class="ml-2">
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import UsergroupsOptions from "./UsergroupsOptions.vue";

export default {
  props: ["usergroup_name", "usergroup_id", "usersubgroup_id"],

  components: { UsergroupsOptions },

  data() {
    return {
      groups: [],
      loading: false,

      // NORMAL MODE
      showModal: false,
      groupName: "",
      editGroupId: null,

      // DUPLICATE
      showDuplicateModal: false,
      duplicateName: "",
      duplicatingGroup: null,

      // USERSUBGROUP MODE
      editMode: false,
      selectedGroupId: null,
      originalGroupId: null,
      swapConfirmText: "",
    };
  },

  computed: {
    modalTitle() {
      return this.editGroupId
        ? "Edit Configuration Group"
        : "Add Configuration Group";
    }
  },

  created() {
    this.fetchGroups();
  },

  methods: {
    async fetchGroups() {
      this.loading = true;
      try {
        const res = await this.$axios.get(
          this.$store.getters.getAPIKey.mainAPI +
          `/usergroups-configuration-groups?usergroup_id=${this.usergroup_id}&usersubgroup_id=${this.usersubgroup_id}`
        );

        this.groups = res.data.groups;

        if (this.usersubgroup_id) {
          this.selectedGroupId = res.data.selected_group_id;
          this.originalGroupId = res.data.selected_group_id;
        }
      } finally {
        this.loading = false;
      }
    },

    /* USERSUBGROUP MODE */
    enableEditMode() {
      this.editMode = true;
    },

    cancelEditMode() {
      this.selectedGroupId = this.originalGroupId;
      this.editMode = false;
    },
    async saveUsersubgroupGroup() {
      await this.$axios.post(
        this.$store.getters.getAPIKey.mainAPI +
        "/usergroups-configuration-groups/usersubgroup-map",
        {
          usersubgroup_id: this.usersubgroup_id,
          programme_config_group_id: this.selectedGroupId
        }
      );

      this.originalGroupId = this.selectedGroupId;
      this.editMode = false;

      this.$toast.success("Configuration Group updated successfully", {
        position: "top",
        duration: 3000,
      });
    },

    /* NORMAL MODE CRUD */
    openAddGroupModal() {
      this.groupName = "";
      this.editGroupId = null;
      this.showModal = true;
    },

    openEditGroupModal(group) {
      this.groupName = group.name;
      this.editGroupId = group.id;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.groupName = "";
      this.editGroupId = null;
    },

    openDuplicateModal(group) {
      this.duplicatingGroup = group;
      this.duplicateName = 'Copy of ' + group.name;
      this.showDuplicateModal = true;
    },

    async confirmDuplicate() {
      if (!this.duplicateName) return;
      await this.$axios.post(
        this.$store.getters.getAPIKey.mainAPI +
        `/usergroups-configuration-groups/${this.duplicatingGroup.id}/duplicate`,
        { name: this.duplicateName, usergroup_id: this.usergroup_id }
      );
      this.showDuplicateModal = false;
      this.duplicatingGroup = null;
      this.duplicateName = '';
      this.fetchGroups();
    },

    async submitGroup() {
      if (!this.groupName) return;

      const payload = {
        name: this.groupName,
        usergroup_id: this.usergroup_id
      };

      if (this.editGroupId) {
        await this.$axios.put(
          this.$store.getters.getAPIKey.mainAPI +
          `/usergroups-configuration-groups/${this.editGroupId}`,
          payload
        );
      } else {
        await this.$axios.post(
          this.$store.getters.getAPIKey.mainAPI +
          `/usergroups-configuration-groups`,
          payload
        );
      }

      this.closeModal();
      this.fetchGroups();
    }
  }
};
</script>
